import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users2, BookOpen, MapPin, Clock, User, Calendar } from 'lucide-react';
import { PageHeader, Badge, PageLoader } from '@repo/ui';

const GroupDetailsPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: group, isLoading } = useQuery({
    queryKey: ['student-group', id],
    queryFn: () => api.get(`/student/groups/${id}`).then((res) => res.data?.data),
    enabled: !!id,
  });

  if (isLoading) {
    return <PageLoader message={t('common.loading')} className="min-h-64" />;
  }

  if (!group) {
    return <div className="text-center py-16 text-muted-foreground">{t('groups.notFound')}</div>;
  }

  // Parse the raw group name to display the core name as the title
  // and all other metadata (schedule, mode, etc.) as beautiful badges.
  const getGroupParts = (name: string) => {
    if (!name) return { title: '', tags: [] };
    const parts = name
      .split('|')
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      return {
        title: `${parts[0]} • ${parts[1]}`, // Keep round and program as the core title
        tags: parts.slice(2), // Everything else as tags
      };
    }
    return { title: name, tags: [] };
  };

  const { title: displayTitle, tags: displayTags } = getGroupParts(group?.name || '');

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/groups')}
        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-black text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all group"
      >
        <ArrowLeft
          className={`h-4 w-4 transition-transform ${isRtl ? 'rotate-180 group-hover:translate-x-1' : 'group-hover:-translate-x-1'}`}
        />
        {t('common.back')}
      </button>

      {/* Standardized Header */}
      <PageHeader
        title={displayTitle}
        subtitle={`${t('common.code')}: ${group.code}`}
        icon={Users2}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      >
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {group.label && (
            <Badge className="px-3 py-1.5 rounded-xl text-xs font-black bg-primary/10 text-primary border-primary/20">
              {group.label.name}
            </Badge>
          )}
          {displayTags.map((tag, idx) => (
            <Badge
              key={idx}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-muted text-muted-foreground border-transparent transition-colors hover:bg-primary hover:text-white"
            >
              {tag}
            </Badge>
          ))}
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {group.program?.track && (
          <div className="flex items-center gap-4 p-5 rounded-[1.5rem] border border-border bg-card/50 shadow-sm">
            <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
              <BookOpen className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-black  text-muted-foreground/60 leading-none mb-1">
                {t('common.track')}
              </p>
              <p className="font-bold text-sm truncate">{group.program.track.name}</p>
            </div>
          </div>
        )}
        {group.branch && (
          <div className="flex items-center gap-4 p-5 rounded-[1.5rem] border border-border bg-card/50 shadow-sm">
            <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
              <MapPin className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-black  text-muted-foreground/60 leading-none mb-1">
                {t('common.branch')}
              </p>
              <p className="font-bold text-sm truncate">{group.branch.nameEn}</p>
            </div>
          </div>
        )}
      </div>

      {/* Schedule */}
      {group.weeklySchedule?.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            {t('student.groups.schedule')}
          </h2>
          <div className="space-y-3">
            {group.weeklySchedule.map((day: any, i: number) => (
              <div
                key={i}
                className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0"
              >
                <span className="font-medium text-sm">
                  {t(`days.${day.day.toUpperCase()}`, { defaultValue: day.day }) as string}
                </span>
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {day.startTime} — {day.endTime}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Staff */}
      {(group.mentor || group.instructor) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {group.mentor && (
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-blue-500/15">
                  <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('student.groups.mentor')}</p>
                  <p className="font-medium">{group.mentor?.userId?.name || group.mentor?.name}</p>
                </div>
              </div>
            </div>
          )}
          {group.instructor && (
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-500/15">
                  <User className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('student.groups.instructor')}</p>
                  <p className="font-medium">
                    {group.instructor?.userId?.name || group.instructor?.name}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupDetailsPage;
